import { useState, useMemo } from 'react';
import { Users, MessageSquare, CheckSquare, Plus, Calendar, CheckCircle2 } from 'lucide-react';
import { DAY_NAMES } from '../../hooks/useOneOnOneRelationships';
import TopicModal from '../TopicModal';
import ActionItemModal from '../ActionItemModal';

// Calculate days until meeting from tomorrow
function getDaysUntil(meetingDay, tomorrowDate) {
  const tomorrow = new Date(tomorrowDate + 'T00:00:00');
  const tomorrowDayOfWeek = tomorrow.getDay();
  if (meetingDay >= tomorrowDayOfWeek) {
    return meetingDay - tomorrowDayOfWeek;
  }
  return 7 - tomorrowDayOfWeek + meetingDay;
}

export default function OneOnOnePrep({
  tomorrowDate,
  weekOneOnOnes = [],
  topicsMap = {},
  actionItemsMap = {},
  sessionsMap = {},
  currentUserName,
  onAddTopic,
  onAddActionItem
}) {
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [showActionItemModal, setShowActionItemModal] = useState(false);
  const [selectedRelationship, setSelectedRelationship] = useState(null);

  // Format tomorrow's date for display
  const tomorrowDisplay = useMemo(() => {
    const date = new Date(tomorrowDate + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    });
  }, [tomorrowDate]);

  const handleAddTopic = (relationship) => {
    setSelectedRelationship(relationship);
    setShowTopicModal(true);
  };

  const handleAddActionItem = (relationship) => {
    setSelectedRelationship(relationship);
    setShowActionItemModal(true);
  };

  const handleSaveTopic = async (data) => {
    if (onAddTopic && selectedRelationship) {
      const session = sessionsMap[selectedRelationship.id];
      await onAddTopic(session?.id, selectedRelationship, data);
    }
    setShowTopicModal(false);
    setSelectedRelationship(null);
  };

  const handleSaveActionItem = async (data) => {
    if (onAddActionItem && selectedRelationship) {
      const session = sessionsMap[selectedRelationship.id];
      await onAddActionItem(session?.id, selectedRelationship, data);
    }
    setShowActionItemModal(false);
    setSelectedRelationship(null);
  };

  // Empty state - no 1:1s this week
  if (weekOneOnOnes.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-surface-hover flex items-center justify-center mb-4">
          <Calendar size={32} className="text-text-muted opacity-50" />
        </div>
        <h3 className="text-lg font-semibold text-text-primary mb-2">
          No 1:1s This Week
        </h3>
        <p className="text-sm text-text-tertiary max-w-xs">
          You don't have any 1:1 meetings scheduled for the rest of this week. You're all set!
        </p>
        <CheckCircle2 size={24} className="text-success mt-4 opacity-70" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-surface-card border-b border-border flex items-center gap-2">
        <Users size={18} className="text-accent" />
        <span className="text-sm font-semibold text-text-primary">
          Prep for This Week's 1:1s
        </span>
        <span className="text-xs text-text-tertiary ml-2">
          {weekOneOnOnes.length} meeting{weekOneOnOnes.length !== 1 ? 's' : ''} remaining
        </span>
      </div>

      {/* Cards grid */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {weekOneOnOnes.map(relationship => {
            const topics = topicsMap[relationship.id] || [];
            const actionItems = actionItemsMap[relationship.id] || [];
            const undiscussedTopics = topics.filter(t => !t.discussed);
            const openActionItems = actionItems.filter(a => !a.completed);
            const daysUntil = getDaysUntil(relationship.meetingDay, tomorrowDate);

            return (
              <div key={relationship.id} className="p-4 bg-surface-card rounded-xl border border-border">
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-accent-muted">
                    <Users size={18} className="text-accent" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-semibold text-text-primary">
                        {relationship.personName}
                      </h4>
                      {daysUntil === 0 ? (
                        <span className="text-[10px] px-1.5 py-0.5 bg-accent/20 text-accent rounded font-medium">
                          Tomorrow
                        </span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 bg-surface-hover text-text-muted rounded">
                          {daysUntil} day{daysUntil !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-text-tertiary">
                      {DAY_NAMES[relationship.meetingDay]} 1:1
                    </p>
                  </div>
                </div>

                {/* Topics Section */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <MessageSquare size={12} className="text-weekly" />
                      <span className="text-xs font-medium text-text-secondary">
                        Topics ({undiscussedTopics.length})
                      </span>
                    </div>
                    <button
                      onClick={() => handleAddTopic(relationship)}
                      className="flex items-center gap-1 px-2 py-1 text-[10px] text-accent bg-accent/10 rounded-md border-none cursor-pointer hover:bg-accent/20 transition-all"
                    >
                      <Plus size={10} />
                      Add
                    </button>
                  </div>

                  {undiscussedTopics.length > 0 ? (
                    <div className="space-y-1.5">
                      {undiscussedTopics.slice(0, 3).map(topic => (
                        <div key={topic.id} className="flex items-start gap-2 p-2 bg-surface-hover rounded-lg">
                          <div className="w-1.5 h-1.5 rounded-full bg-weekly mt-1.5 flex-shrink-0" />
                          <p className="text-xs text-text-secondary line-clamp-2">{topic.text}</p>
                        </div>
                      ))}
                      {undiscussedTopics.length > 3 && (
                        <p className="text-[10px] text-text-muted pl-3.5">
                          +{undiscussedTopics.length - 3} more
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-[10px] text-text-tertiary italic pl-3.5">
                      No topics to discuss yet
                    </p>
                  )}
                </div>

                {/* Action Items Section */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <CheckSquare size={12} className="text-accent" />
                      <span className="text-xs font-medium text-text-secondary">
                        Open Actions ({openActionItems.length})
                      </span>
                    </div>
                    <button
                      onClick={() => handleAddActionItem(relationship)}
                      className="flex items-center gap-1 px-2 py-1 text-[10px] text-accent bg-accent/10 rounded-md border-none cursor-pointer hover:bg-accent/20 transition-all"
                    >
                      <Plus size={10} />
                      Add
                    </button>
                  </div>

                  {openActionItems.length > 0 ? (
                    <div className="space-y-1.5">
                      {openActionItems.slice(0, 3).map(item => (
                        <div key={item.id} className="flex items-start gap-2 p-2 bg-surface-hover rounded-lg">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent mt-1.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-text-secondary line-clamp-1">{item.text}</p>
                            <p className="text-[10px] text-text-muted">{item.assignedTo}</p>
                          </div>
                        </div>
                      ))}
                      {openActionItems.length > 3 && (
                        <p className="text-[10px] text-text-muted pl-3.5">
                          +{openActionItems.length - 3} more
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-[10px] text-text-tertiary italic pl-3.5">
                      No open action items
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modals */}
      <TopicModal
        isOpen={showTopicModal}
        onClose={() => {
          setShowTopicModal(false);
          setSelectedRelationship(null);
        }}
        onSave={handleSaveTopic}
        currentUserName={currentUserName}
      />

      <ActionItemModal
        isOpen={showActionItemModal}
        onClose={() => {
          setShowActionItemModal(false);
          setSelectedRelationship(null);
        }}
        onSave={handleSaveActionItem}
        relationshipPersonName={selectedRelationship?.personName}
      />
    </div>
  );
}
